import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Loader2,
  Menu,
  X,
  AlertTriangle,
  Info,
  LogOut,
  Bookmark,
  HelpCircle,
  Bot,
  Grid,
  GitFork,
  FolderTree,
  RefreshCw,
  Plug,
  Settings,
  MoreHorizontal,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { CommandPalette } from "@/components/CommandPalette";
import MessageThread from "@/components/MessageThread";
import PermissionPrompts from "@/components/PermissionPrompts";
import PromptBox from "@/components/PromptBox";
import SessionList from "@/components/SessionList";
import SessionInfoSheet from "@/components/SessionInfoSheet";
import { SidebarNav } from "@/components/SidebarNav";
import Login from "@/components/Login";
import MarkersPanel from "@/components/MarkersPanel";
import OnboardingSheet, { getOnboardingDone } from "@/components/OnboardingSheet";
import { McpMonitorPanel } from "@/components/McpMonitorPanel";
import { SettingsPanel } from "@/components/SettingsPanel";
import { FileExplorerPanel } from "@/components/FileExplorerPanel";
import { HomeView } from "@/views/HomeView";
import { MachinesView } from "@/views/MachinesView";
import { WelcomeView, getWelcomeDone } from "@/views/WelcomeView";
import { ProjectsView } from "@/views/projects/ProjectsView";
import { ProjectDetailView } from "@/views/projects/ProjectDetailView";
import { AssistantsView } from "@/views/assistants/AssistantsView";
import { AssistantDetailView } from "@/views/assistants/AssistantDetailView";
import { ServersView } from "@/views/servers/ServersView";
import { ServerDetailView } from "@/views/servers/ServerDetailView";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TooltipProvider } from "@/components/ui/tooltip";
import { findSession, pickPrimaryMachineId, store, useStore, type AppView } from "@/lib/store";
import { useEventStream } from "@/hooks/useEventStream";
import { useHubStream } from "@/hooks/useHubStream";
import { useUrlSync } from "@/hooks/useUrlSync";
import { authApi } from "@/lib/authClient";
import { getHubAccessToken, cachedHubToken, hasHubSession } from "@/lib/hubAuth";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

function ErrorBanner() {
  const state = useStore();
  const entries = useMemo(() => {
    return Object.entries(state.errors)
      .filter(([, v]) => !!v)
      .slice(-3);
  }, [state.errors]);

  if (entries.length === 0) return null;

  return (
    <div className="shrink-0 border-b border-destructive/40 bg-destructive/10">
      {entries.map(([key, message]) => (
        <div
          key={key}
          className="flex items-start gap-2 px-3 sm:px-4 py-2 text-xs"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive mt-0.5" />
          <div className="flex-1 min-w-0 text-destructive/90">
            <span className="font-mono text-destructive/60">{key}:</span>{" "}
            <span className="break-words">{message}</span>
          </div>
          <button
            type="button"
            onClick={() => store.clearError(key)}
            className="text-destructive/60 hover:text-destructive shrink-0"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

function StatusPill({
  status,
  channelPresent,
}: {
  status: "live" | "mirror" | "idle" | undefined;
  channelPresent?: boolean;
}) {
  if (!status || status === "idle") return null;
  if (status === "live") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-primary/40 bg-primary/10 text-primary font-mono">
        <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_hsl(var(--primary))]" />
        live
      </span>
    );
  }
  if (status === "mirror" && channelPresent) {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-accent/40 bg-accent/10 text-accent font-mono">
          <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
          mirror
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-primary/40 bg-primary/10 text-primary font-mono">
          <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_hsl(var(--primary))]" />
          channel
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-accent/40 bg-accent/10 text-accent font-mono">
      <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
      mirror
    </span>
  );
}

// Shared look for the square icon actions in the header.
const HEADER_ICON_BUTTON =
  "h-9 w-9 flex items-center justify-center rounded-md hover:bg-secondary/50 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:pointer-events-none";

// How long to wait for the server to answer /api/health again after a restart.
const RESTART_HEALTH_TIMEOUT_MS = 30_000;

function Header({
  onOpenSidebar,
  onOpenInfo,
  onLogout,
  onOpenMarkers,
  onOpenOnboarding,
  onOpenMcpMonitor,
  onOpenSettings,
  onOpenFiles,
  teamDashboardCount,
  mcpCallCount,
}: {
  onOpenSidebar: () => void;
  onOpenInfo: () => void;
  onLogout: () => void;
  onOpenMarkers: () => void;
  onOpenOnboarding: () => void;
  onOpenMcpMonitor: () => void;
  onOpenSettings: () => void;
  onOpenFiles: () => void;
  teamDashboardCount: number;
  mcpCallCount: number;
}) {
  const [disposeOpen, setDisposeOpen] = useState(false);
  const [restartOpen, setRestartOpen] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [restartError, setRestartError] = useState<string | null>(null);
  const state = useStore();
  const { connected: localConnected } = useEventStream();
  // In hosted mode the realtime link is the hub WebSocket, not the local SSE.
  const connected = localConnected || (state.hubEnabled && state.hubConnected);
  const sessionId = state.selected.sessionId;
  const slug = state.selected.slug;

  const session = useMemo(() => {
    return findSession(state, state.selected.source, slug, sessionId);
  }, [state, slug, sessionId]);

  // Forking is a local-server operation; remote (hub) sessions cannot fork.
  const canFork = !!session && !!slug && state.selected.source === "local";

  function handleFork() {
    if (!canFork || !session || !slug) return;
    store.forkSession(slug, session.id).catch(() => {});
  }

  async function handleRestart() {
    setRestarting(true);
    setRestartError(null);
    try {
      await api.restartServer();
    } catch (err) {
      setRestarting(false);
      setRestartError(err instanceof Error ? err.message : String(err));
      return;
    }
    // The process only drops after it answers the restart request, so give it a
    // beat before polling — otherwise the first health probe hits the old server.
    const deadline = Date.now() + RESTART_HEALTH_TIMEOUT_MS;
    await new Promise((resolve) => setTimeout(resolve, 2000));
    while (Date.now() < deadline) {
      try {
        const res = await fetch("/api/health", { cache: "no-store" });
        if (res.ok) {
          window.location.reload();
          return;
        }
      } catch {
        // Still down — keep polling until the deadline.
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    setRestarting(false);
    setRestartError("Server did not come back in time. Reload once it is up again.");
  }

  return (
    <header className="h-14 shrink-0 border-b border-border/40 glass px-2 sm:px-4 flex items-center gap-2">
      <button
        type="button"
        onClick={onOpenSidebar}
        className="md:hidden h-9 w-9 flex items-center justify-center rounded-md hover:bg-secondary/50 text-foreground"
        aria-label="Open sessions"
      >
        <Menu className="h-5 w-5" />
      </button>
      {/* Never let the action row squeeze the title away entirely on narrow screens. */}
      <div className="flex-1 basis-0 min-w-[7rem] overflow-hidden">
        {session ? (
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate flex items-center gap-2">
              <span className="truncate">
                {session.title || session.id.slice(0, 12)}
              </span>
              <StatusPill
                status={session.status}
                channelPresent={session?.channel?.present}
              />
            </div>
            <div className="text-[11px] text-muted-foreground font-mono truncate">
              {session.cwd}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground truncate">
            No session selected
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        <div
          className={`hidden sm:flex items-center gap-1.5 text-[10px] uppercase tracking-wide ${
            connected ? "text-primary" : "text-muted-foreground"
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              connected
                ? "bg-primary shadow-[0_0_8px_hsl(var(--primary))]"
                : "bg-muted-foreground"
            }`}
          />
          {connected ? "connected" : "offline"}
        </div>
        <span
          className={cn(
            "sm:hidden h-2 w-2 rounded-full",
            connected
              ? "bg-primary shadow-[0_0_6px_hsl(var(--primary))]"
              : "bg-muted-foreground",
          )}
          aria-label={connected ? "connected" : "offline"}
        />
        {session && (
          <button
            type="button"
            onClick={onOpenInfo}
            className={HEADER_ICON_BUTTON}
            aria-label="Session info"
          >
            <Info className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={onOpenMarkers}
          className={HEADER_ICON_BUTTON}
          aria-label="Markers"
        >
          <Bookmark className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onOpenFiles}
          className={HEADER_ICON_BUTTON}
          aria-label="Files"
          title="Browse files"
        >
          <FolderTree className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onOpenMcpMonitor}
          className={cn(HEADER_ICON_BUTTON, "relative")}
          aria-label="MCP Activity"
          title="MCP Activity"
        >
          <Plug className={cn(
            "h-4 w-4",
            mcpCallCount > 0 ? "text-primary" : "text-muted-foreground"
          )} />
          {mcpCallCount > 0 && (
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary animate-pulse" />
          )}
        </button>
        <button
          type="button"
          onClick={() => store.navigate({ name: "machines" })}
          className={cn(HEADER_ICON_BUTTON, "relative")}
          aria-label="Machines"
          title="Machines"
        >
          <Bot className={cn(
            "h-4 w-4",
            teamDashboardCount > 0 ? "text-primary" : "text-muted-foreground"
          )} />
          {teamDashboardCount > 0 && (
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary animate-pulse" />
          )}
        </button>

        {/* Secondary actions: inline on wide screens, folded into the overflow
            menu below lg so the header never clips on a phone. */}
        <div className="hidden lg:flex items-center gap-1 sm:gap-2">
          {session && (
            <button
              type="button"
              onClick={handleFork}
              className={HEADER_ICON_BUTTON}
              aria-label="Fork session"
              title="Fork into new session"
              disabled={!canFork}
            >
              <GitFork className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => store.navigate({ name: "projects" })}
            className={HEADER_ICON_BUTTON}
            aria-label="Projects"
            title="Projects"
          >
            <Grid className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onOpenSettings}
            className={HEADER_ICON_BUTTON}
            aria-label="Services & settings"
            title="Services & settings"
          >
            <Settings className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onOpenOnboarding}
            className={HEADER_ICON_BUTTON}
            aria-label="Help"
            title="Help"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          {session && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDisposeOpen(true)}
            >
              Disconnect
            </Button>
          )}
          <button
            type="button"
            onClick={() => setRestartOpen(true)}
            className={HEADER_ICON_BUTTON}
            aria-label="Restart server"
            title="Restart server"
          >
            <RefreshCw className={cn("h-4 w-4", restarting && "animate-spin")} />
          </button>
          <button
            type="button"
            onClick={onLogout}
            className={HEADER_ICON_BUTTON}
            aria-label="Log out"
            title="Log out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(HEADER_ICON_BUTTON, "lg:hidden")}
              aria-label="More actions"
              title="More actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {session && (
              <DropdownMenuItem disabled={!canFork} onSelect={handleFork}>
                <GitFork className="mr-2 h-4 w-4" />
                Fork session
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => store.navigate({ name: "projects" })}>
              <Grid className="mr-2 h-4 w-4" />
              Projects
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onOpenSettings}>
              <Settings className="mr-2 h-4 w-4" />
              Services &amp; settings
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onOpenOnboarding}>
              <HelpCircle className="mr-2 h-4 w-4" />
              Help
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {session && (
              <DropdownMenuItem onSelect={() => setDisposeOpen(true)}>
                <X className="mr-2 h-4 w-4" />
                Disconnect session
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => setRestartOpen(true)}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Restart server
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {session && (
          <Dialog open={disposeOpen} onOpenChange={setDisposeOpen}>
            <DialogContent className="glass glass-border">
              <DialogHeader>
                <DialogTitle>Dispose session?</DialogTitle>
                <DialogDescription>
                  &ldquo;{session.title || session.id.slice(0, 12)}&rdquo; will be closed. The JSONL file will be kept.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="ghost" size="sm">Keep</Button>
                </DialogClose>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setDisposeOpen(false);
                    store.disposeSession(session.id);
                  }}
                >
                  Dispose
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        <Dialog
          open={restartOpen}
          onOpenChange={(next) => {
            // Stay open while the restart is in flight so the progress and any
            // failure remain visible.
            if (restarting) return;
            setRestartOpen(next);
            if (!next) setRestartError(null);
          }}
        >
          <DialogContent className="glass glass-border">
            <DialogHeader>
              <DialogTitle>Restart server?</DialogTitle>
              <DialogDescription>
                The CodeZero server will restart. Active sessions will be disposed and the page will reconnect automatically.
              </DialogDescription>
            </DialogHeader>
            {restarting && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Restarting… waiting for the server to come back.
              </div>
            )}
            {restartError && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive/90">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span className="break-words">{restartError}</span>
              </div>
            )}
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="ghost" size="sm" disabled={restarting}>
                  {restartError ? "Close" : "Cancel"}
                </Button>
              </DialogClose>
              <Button
                variant="default"
                size="sm"
                disabled={restarting}
                onClick={handleRestart}
              >
                {restarting ? "Restarting…" : restartError ? "Try again" : "Restart"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </header>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const state = useStore();
  const loadingProjects =
    !state.projectsLoaded && !state.errors.projects;

  return (
    <div className="flex flex-col h-full w-full">
      <div className="h-14 shrink-0 flex items-center px-4 border-b border-border/40">
        <BrandLogo size="md" showTagline />
      </div>
      <SidebarNav onNavigate={onNavigate} />
      <div className="flex-1 min-h-0">
        {loadingProjects ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-xs">Loading projects</span>
          </div>
        ) : (
          <SessionList />
        )}
      </div>
    </div>
  );
}

const VIEW_TITLES: Record<AppView["name"], string> = {
  home: "Home",
  welcome: "Get started",
  console: "Sessions",
  projects: "Projects",
  project: "Project",
  assistants: "Assistants",
  assistant: "Assistant",
  servers: "MCP Servers",
  server: "MCP Server",
  machines: "Machines",
};

// Detail views get a back affordance to their parent list.
function parentViewOf(view: AppView): AppView | null {
  switch (view.name) {
    case "project":
      return { name: "projects" };
    case "assistant":
      return { name: "assistants" };
    case "server":
      return { name: "servers" };
    default:
      return null;
  }
}

function PlatformHeader({
  view,
  onOpenSidebar,
  onLogout,
}: {
  view: AppView;
  onOpenSidebar: () => void;
  onLogout: () => void;
}) {
  const parent = parentViewOf(view);
  return (
    <header className="h-14 shrink-0 border-b border-border/40 glass px-2 sm:px-4 flex items-center gap-2">
      <button
        type="button"
        onClick={onOpenSidebar}
        className="md:hidden h-9 w-9 flex items-center justify-center rounded-md hover:bg-secondary/50 text-foreground"
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </button>
      {parent && (
        <button
          type="button"
          onClick={() => store.navigate(parent)}
          className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-secondary/50 text-muted-foreground hover:text-foreground"
          aria-label={`Back to ${VIEW_TITLES[parent.name]}`}
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
      )}
      <div className="flex-1 min-w-0 text-sm font-semibold truncate">
        {VIEW_TITLES[view.name]}
      </div>
      <button
        type="button"
        onClick={onLogout}
        className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-secondary/50 text-muted-foreground hover:text-foreground"
        aria-label="Log out"
        title="Log out"
      >
        <LogOut className="h-4 w-4" />
      </button>
    </header>
  );
}

function PlatformViewBody({
  view,
  onNewSession,
}: {
  view: AppView;
  onNewSession: () => void;
}) {
  switch (view.name) {
    case "home":
      return <HomeView onNewSession={onNewSession} />;
    case "welcome":
      return <WelcomeView />;
    case "projects":
      return <ProjectsView />;
    case "project":
      return <ProjectDetailView id={view.id} />;
    case "assistants":
      return <AssistantsView />;
    case "assistant":
      return <AssistantDetailView id={view.id} />;
    case "servers":
      return <ServersView />;
    case "server":
      return <ServerDetailView id={view.id} />;
    case "machines":
      return <MachinesView />;
    default:
      return null;
  }
}

// Docked bottom overlays (markers, MCP monitor). Height is per-panel; the
// safe-area padding keeps the footer clear of the iOS home indicator.
const BOTTOM_PANEL_CLASS =
  "fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-xl z-30 pb-[env(safe-area-inset-bottom)]";

function MainApp({ onLogout }: { onLogout: () => void }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [markersOpen, setMarkersOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [mcpMonitorOpen, setMcpMonitorOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const state = useStore();
  const sessionId = state.selected.sessionId;
  const runningTaskCount = state.runningTasks.filter((t) => t.state === "running").length;
  const activeMcpCallCount = state.mcpCalls.filter((c) => c.state === "running").length;

  useUrlSync();
  useHubStream();

  useEffect(() => {
    // Load local projects/markers unconditionally and in parallel with the
    // provider lookup below — never gated on it, so a transient /api/auth/provider
    // failure can't wedge the sidebar or block deep-link hydration. Hosted mode
    // (served from the hub Worker, identified by a hub.mat token in localStorage)
    // has no local bun server, so these same-origin calls would 401; skip them
    // and mark the local project list as resolved so the sidebar stops spinning.
    if (hasHubSession()) {
      store.markProjectsLoaded();
      return;
    }
    store.loadProjects();
    store.loadMarkers();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const provider = await authApi.provider();
      if (cancelled) return;

      if (provider === "authkit-pkce") {
        // Hosted at code.opzero.sh: the mat_ token from the auth gate's PKCE flow
        // is the session and there is no "this machine". Drive the UI off the
        // federated machine list; local projects/markers are skipped above.
        const token = cachedHubToken();
        store.enterHostedMode(token);
        if (token) store.loadRemoteMachines().catch(() => {});
        return;
      }

      if (provider === "authkit") {
        const token = await authApi.hubToken();
        if (cancelled) return;
        store.setHubAuth(!!token?.accessToken, token?.accessToken ?? null, token?.machineId ?? null);
        if (token?.accessToken) {
          store.loadRemoteMachines().catch(() => {});
        }
      } else {
        store.setHubAuth(false, null);
      }
    })().catch(() => {
      if (cancelled) return;
      // A provider() failure must not strand hosted mode on a local spinner.
      if (hasHubSession()) {
        const token = cachedHubToken();
        store.enterHostedMode(token);
        if (token) store.loadRemoteMachines().catch(() => {});
      } else {
        store.setHubAuth(false, null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // New accounts arriving from the opzero.sh signup flow reach the hosted
  // dashboard with nothing registered — no machines, no sessions. Send them to
  // the welcome fork (install CodeZero vs. add the Claude.ai connector) instead
  // of an empty console. Once per browser, and only while they are still on the
  // default view, so a deep link or their own navigation always wins.
  const welcomeRoutedRef = useRef(false);
  const hostedMode = state.hostedMode;
  const remoteMachinesLoaded = state.remoteMachinesLoaded;
  const machineCount = Object.keys(state.remote).length;
  const machinesError = state.errors.remoteMachines;
  useEffect(() => {
    if (welcomeRoutedRef.current) return;
    if (!hostedMode || !remoteMachinesLoaded) return;
    // loadRemoteMachines() also flips remoteMachinesLoaded on its failure path,
    // leaving the map empty — a transient hub/network error looks exactly like
    // a brand-new account. Only a clean resolution proves "nothing registered",
    // and bailing without latching lets a later retry route instead.
    if (machinesError) return;
    welcomeRoutedRef.current = true;
    if (machineCount > 0 || getWelcomeDone()) return;
    if (store.getSnapshot().view.name !== "home") return;
    store.navigate({ name: "welcome" });
  }, [hostedMode, remoteMachinesLoaded, machineCount, machinesError]);

  useEffect(() => {
    if (!state.projectsLoaded) return;
    if (getOnboardingDone()) return;
    // In hosted mode the welcome fork IS the first-run surface, so the sheet
    // stays behind the Help button until the welcome is dismissed — otherwise
    // the two stack. state.hostedMode is the wrong gate here: it only flips
    // after the async provider lookup, while the hosted boot path marks
    // projectsLoaded immediately. hasHubSession() is the same synchronous
    // localStorage tell that boot path uses, so it resolves the mode in time.
    if (hasHubSession() && !getWelcomeDone()) return;
    setOnboardingOpen(true);
  }, [state.projectsLoaded]);

  const platformAvailable = state.platform.available;
  useEffect(() => {
    // Fires once per availability flip: covers hosted-mode boot, local authkit
    // token arrival, and post-connect after the cookie-mode PKCE round trip.
    if (platformAvailable) {
      store.loadPlatformOverview().catch(() => {});
    }
  }, [platformAvailable]);

  useEffect(() => {
    if (sessionId) setDrawerOpen(false);
  }, [sessionId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && drawerOpen) {
        setDrawerOpen(false);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  useEffect(() => {
    // Escape closes whichever docked bottom panel is up (they are plain
    // overlays, not Radix dialogs, so they get no dismissal for free).
    if (!markersOpen && !mcpMonitorOpen && !filesOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setMarkersOpen(false);
      setMcpMonitorOpen(false);
      setFilesOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [markersOpen, mcpMonitorOpen, filesOpen]);

  function handleNewSession() {
    if (state.hostedMode) {
      // No local projects in hosted mode; spawn on the primary machine (same
      // selection as the New Session dialog's default + the sidebar's primary).
      const machineId = pickPrimaryMachineId(state);
      const project = machineId ? state.remote[machineId]?.projects[0] : undefined;
      if (machineId && project) {
        store.createRemoteSession(machineId, project.slug).catch(() => {});
      }
      return;
    }
    const firstProject = state.projects[0];
    if (firstProject) {
      store.createSession(firstProject.slug).catch(() => {});
    }
  }

  function handleToggleSidebar() {
    setDrawerOpen((v) => !v);
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="flex bg-background text-foreground overflow-hidden"
        style={{
          height: "100dvh",
          width: "100vw",
          paddingTop: "env(safe-area-inset-top)",
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
        }}
      >
        <aside className="hidden md:flex w-[280px] shrink-0 flex-col border-r border-border/40 glass glass-border">
          <Sidebar />
        </aside>

        {drawerOpen && (
          <>
            <div
              className="md:hidden fixed inset-0 bg-black/60 z-40"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <aside
              className="md:hidden fixed inset-y-0 left-0 z-50 w-[82vw] max-w-[320px] flex flex-col border-r border-border/40 bg-card glass-border shadow-2xl"
              role="dialog"
              aria-label="Sessions"
            >
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center rounded-md hover:bg-secondary/50 text-muted-foreground"
                aria-label="Close sessions"
              >
                <X className="h-4 w-4" />
              </button>
              <Sidebar onNavigate={() => setDrawerOpen(false)} />
            </aside>
          </>
        )}

        <main className="flex-1 min-w-0 flex flex-col">
          {state.view.name === "console" ? (
            <>
              <Header
                onOpenSidebar={() => setDrawerOpen(true)}
                onOpenInfo={() => setInfoOpen(true)}
                onLogout={onLogout}
                onOpenMarkers={() => setMarkersOpen(true)}
                onOpenOnboarding={() => setOnboardingOpen(true)}
                onOpenMcpMonitor={() => setMcpMonitorOpen(true)}
                onOpenSettings={() => setSettingsOpen(true)}
                onOpenFiles={() => setFilesOpen(true)}
                teamDashboardCount={runningTaskCount}
                mcpCallCount={activeMcpCallCount}
              />
              <ErrorBanner />
              <PermissionPrompts />
              <MessageThread />
              <PromptBox />
            </>
          ) : (
            <>
              <PlatformHeader
                view={state.view}
                onOpenSidebar={() => setDrawerOpen(true)}
                onLogout={onLogout}
              />
              <ErrorBanner />
              <div
                className="flex-1 min-h-0 overflow-y-auto"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
              >
                <PlatformViewBody view={state.view} onNewSession={handleNewSession} />
              </div>
            </>
          )}
        </main>

        <SessionInfoSheet open={infoOpen} onOpenChange={setInfoOpen} />

        <OnboardingSheet open={onboardingOpen} onOpenChange={setOnboardingOpen} />

        <CommandPalette
          open={commandPaletteOpen}
          onOpenChange={setCommandPaletteOpen}
          onNewSession={handleNewSession}
          onToggleSidebar={handleToggleSidebar}
          onOpenInfo={() => setInfoOpen(true)}
          onLogout={onLogout}
        />

        {markersOpen && (
          <div className={cn(BOTTOM_PANEL_CLASS, "h-[min(320px,70vh)]")}>
            <MarkersPanel onClose={() => setMarkersOpen(false)} />
          </div>
        )}

        {mcpMonitorOpen && (
          <div className={cn(BOTTOM_PANEL_CLASS, "h-[min(400px,70vh)]")}>
            <McpMonitorPanel onClose={() => setMcpMonitorOpen(false)} />
          </div>
        )}

        {settingsOpen && (
          <SettingsPanel onClose={() => setSettingsOpen(false)} />
        )}

        {filesOpen && (
          <FileExplorerPanel onClose={() => setFilesOpen(false)} />
        )}
      </div>
    </TooltipProvider>
  );
}

type AuthState =
  | { phase: "checking" }
  | { phase: "unauthed" }
  | { phase: "authed"; sub: string };

function App() {
  const [auth, setAuth] = useState<AuthState>({ phase: "checking" });
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const provider = await authApi.provider();
      if (cancelled) return;
      if (provider === "authkit-pkce") {
        // Hosted at code.opzero.sh: the MCPAuthKit mat_ token is the session,
        // there is no cookie login. getHubAccessToken() may navigate the page to
        // MCPAuthKit (and never resolve) when a fresh authorization is needed.
        const token = await getHubAccessToken();
        if (cancelled) return;
        setAuth(token ? { phase: "authed", sub: "hub" } : { phase: "unauthed" });
        return;
      }
      const user = await authApi.me();
      if (!cancelled) {
        setAuth(user ? { phase: "authed", sub: user.sub } : { phase: "unauthed" });
      }
    })().catch(() => {
      if (!cancelled) setAuth({ phase: "unauthed" });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleLogout() {
    try {
      await authApi.logout();
    } catch {
      // ignore
    }
    setAuth({ phase: "unauthed" });
  }

  if (auth.phase === "checking") {
    return (
      <div
        className="flex items-center justify-center bg-background text-muted-foreground"
        style={{ height: "100dvh", width: "100vw" }}
      >
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (auth.phase === "unauthed") {
    return (
      <>
        <Login
          onAuthed={async () => {
            const user = await authApi.me();
            if (user) setAuth({ phase: "authed", sub: user.sub });
          }}
          onOpenOnboarding={() => setOnboardingOpen(true)}
        />
        <OnboardingSheet open={onboardingOpen} onOpenChange={setOnboardingOpen} />
      </>
    );
  }

  return <MainApp onLogout={handleLogout} />;
}

export default App;
